import React, { useState } from "react";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Table as TableIcon,
  Loader2,
  Check,
} from "lucide-react";
import { downloadAssistantFile } from "@/utils";
import "./AssistantDownloadCard.css";

export default function AssistantDownloadCard({
  href,
  children,
  filename: directFilename,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // Extract clean display label / filename
  const rawText =
    typeof children === "string"
      ? children
      : Array.isArray(children) && typeof children[0] === "string"
        ? children.join(" ")
        : "";

  let displayFilename = directFilename || rawText || "Download File";
  displayFilename =
    displayFilename.replace(/^Download\s+/i, "").trim() || "Download File";

  // Determine file type / extension
  const cleanHref = (href || "").split("?")[0];
  const extMatch = (displayFilename || cleanHref).match(
    /\.(xlsx|xls|csv|docx|pdf|md|txt|json)$/i,
  );
  const ext = extMatch ? extMatch[1].toUpperCase() : "FILE";

  const renderIcon = () => {
    switch (ext) {
      case "XLSX":
      case "XLS":
        return <FileSpreadsheet size={18} className="assistant-file-icon xlsx" />;
      case "CSV":
        return <TableIcon size={18} className="assistant-file-icon csv" />;
      case "MD":
      case "TXT":
      case "DOCX":
      case "PDF":
        return <FileText size={18} className="assistant-file-icon doc" />;
      default:
        return <FileText size={18} className="assistant-file-icon default" />;
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const success = await downloadAssistantFile(href, displayFilename);
      if (success) {
        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 3000);
      }
    } catch {
      // Error handled inside downloadAssistantFile
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="assistant-download-card"
      data-testid="assistant-download-card"
    >
      <div className="assistant-download-card-info">
        <div className="assistant-download-card-icon-wrapper">{renderIcon()}</div>
        <div className="assistant-download-card-details">
          <span
            className="assistant-download-card-filename"
            title={displayFilename}
          >
            {displayFilename}
          </span>
          <span className="assistant-download-card-badge">{ext}</span>
        </div>
      </div>
      <button
        type="button"
        className={`assistant-download-card-btn ${downloaded ? "success" : ""}`}
        onClick={handleDownload}
        disabled={isDownloading}
        aria-label={`Download ${displayFilename}`}
        data-testid="assistant-download-btn"
      >
        {isDownloading ? (
          <>
            <Loader2 size={13} className="assistant-download-spin" />
            <span>Downloading...</span>
          </>
        ) : downloaded ? (
          <>
            <Check size={13} />
            <span>Downloaded</span>
          </>
        ) : (
          <>
            <Download size={13} />
            <span>Download</span>
          </>
        )}
      </button>
    </div>
  );
}
